import React, { useEffect, useMemo, useRef, useState } from "react";
import logo from "../assets/DesarrollandoTerritorios.png"
import html2pdf from "html2pdf.js";
import EnviarAlmacen from "./EnviarAlmacen";
import { apiConfig } from "../config/api.config";

// =========================
// Helpers números CO
// =========================
const formatNumberCO = (v) => {
  if (v === null || v === undefined) return "";

  const direct = Number(v);
  if (Number.isFinite(direct)) {
    return new Intl.NumberFormat("es-CO", { maximumFractionDigits: 0 }).format(direct);
  }

  let s = String(v).trim();
  if (!s) return "";

  s = s.replace(/[^\d,.\-]/g, "");

  const dotCount = (s.match(/\./g) || []).length;
  const commaCount = (s.match(/,/g) || []).length;

  if (commaCount === 0 && dotCount > 1) s = s.replace(/\./g, "");
  if (commaCount === 1 && dotCount >= 1) s = s.replace(/\./g, "").replace(",", ".");
  if (commaCount > 1 && dotCount === 0) s = s.replace(/,/g, "");
  if (commaCount === 1 && dotCount === 0) s = s.replace(",", ".");

  const n = Number(s);
  if (!Number.isFinite(n)) return "";

  return new Intl.NumberFormat("es-CO", { maximumFractionDigits: 0 }).format(n);
};

const parseNumberCO = (val) => {
  if (val === null || val === undefined) return NaN;
  let s = String(val).trim();
  if (!s) return NaN;

  s = s.replace(/[^\d,.\-]/g, "");

  const dotCount = (s.match(/\./g) || []).length;
  const commaCount = (s.match(/,/g) || []).length;

  if (commaCount === 0 && dotCount > 1) s = s.replace(/\./g, "");
  if (commaCount === 1 && dotCount >= 1) s = s.replace(/\./g, "").replace(",", ".");
  if (commaCount > 1 && dotCount === 0) s = s.replace(/,/g, "");
  if (commaCount === 1 && dotCount === 0) s = s.replace(",", ".");

  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
};

export default function AlmacenCertificadoModal(props) {
  // Compatibilidad props
  const isOpenProp = Boolean(props.isOpen ?? props.show);
  const onClose = props.onClose;
  const contractData = props.contractData ?? props.contrato;
  const userRole = props.userRole;
  const onResponded = props.onResponded;

  const esAlmacen = userRole === "almacen" || userRole === "administrador";

  const [internalOpen, setInternalOpen] = useState(false);

  // ✅ Bloquea reabrir si el padre deja isOpen=true
  const [forceClosed, setForceClosed] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mensaje, setMensaje] = useState("");
  const [formData, setFormData] = useState(null);

  // ✅ guardamos la solicitud real (con estado / adjuntos respuesta / motivo / fechas)
  const [solicitud, setSolicitud] = useState(null);

  const [isPrinting, setIsPrinting] = useState(false);
  const modalRef = useRef(null);

  // Modal extra (si lo usas)
  const [showResponderAlmacen, setShowResponderAlmacen] = useState(false);

  // Rechazo
  const [showReject, setShowReject] = useState(false);
  const [motivoRechazo, setMotivoRechazo] = useState("");

  // Campos monetarios
  const moneyFields = useMemo(() => new Set(["valorSolicitado", "valorEstimadoVigencia"]), []);

  const safeJson = async (resp) => {
    const text = await resp.text().catch(() => "");
    try {
      return text ? JSON.parse(text) : {};
    } catch {
      return { raw: text };
    }
  };

  const normalizeId = (id) => {
    if (!id) return "";
    return String(id).replace(/[{}]/g, "").replace(/\s+/g, "").trim();
  };

  // ✅ Reset del forceClosed cuando cambia el contrato
  const contractKey = normalizeId(
    contractData?.contract_id ||
      contractData?.contractId ||
      contractData?.id_contract ||
      contractData?.id ||
      ""
  );

  useEffect(() => {
    setForceClosed(false);
  }, [contractKey]);

  // Sync open/close (pero respetando forceClosed)
  useEffect(() => {
    if (isOpenProp && !forceClosed) setInternalOpen(true);
    if (!isOpenProp) setInternalOpen(false);
  }, [isOpenProp, forceClosed]);

  const isOpen = internalOpen && !forceClosed;

  const closeModalSafe = () => {
    setInternalOpen(false);
    setForceClosed(true);
    setShowReject(false);
    if (typeof onClose === "function") onClose();
  };

  // =========================
  // URL + Download robusto
  // =========================
  const resolveFileUrl = (rawUrl) => {
    if (!rawUrl) return "";
    let u = String(rawUrl).trim();
    if (!u) return "";

    if (/^https?:\/\//i.test(u)) {
      if (u.includes("/uploads/") && !u.includes("/api/uploads/")) {
        u = u.replace("/uploads/", "/api/uploads/");
      }
      return u;
    }

    if (!u.startsWith("/")) u = "/" + u;
    if (u.startsWith("/uploads/")) u = "/api" + u;

    if (u.startsWith("/api/")) return apiConfig.getUrl(u);
    return apiConfig.getUrl(u);
  };

  const handleDownload = async (url, name) => {
    try {
      const downloadUrl = resolveFileUrl(url);
      if (!downloadUrl) throw new Error("URL inválida para descarga.");

      const response = await fetch(downloadUrl, {
        credentials: "include",
        redirect: "follow",
        headers: { Accept: "application/pdf,application/octet-stream;q=0.9,*/*;q=0.8" },
      });

      if (!response.ok) {
        const txt = await response.text().catch(() => "");
        throw new Error(`HTTP ${response.status}. ${txt.slice(0, 180)}`);
      }

      const contentType = (response.headers.get("content-type") || "").toLowerCase();
      if (contentType.includes("text/html")) {
        const txt = await response.text().catch(() => "");
        throw new Error(`El servidor devolvió HTML (login/redirect/404). Muestra: ${txt.slice(0, 140)}`);
      }

      const blob = await response.blob();
      if (!blob || blob.size < 50) throw new Error("Archivo vacío o demasiado pequeño.");

      const head = await blob.slice(0, 5).text().catch(() => "");
      if (head !== "%PDF-") {
        const peek = await blob.slice(0, 120).text().catch(() => "");
        throw new Error(`Lo descargado no parece PDF. Firma: "${head}". Muestra: ${peek.slice(0, 120)}`);
      }

      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;

      let safeName = (name && String(name).trim()) || "archivo.pdf";
      if (!safeName.toLowerCase().endsWith(".pdf")) safeName += ".pdf";
      link.download = safeName;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("❌ Error al descargar:", err);
      alert(`Error al descargar.\n\n${err?.message || "Desconocido"}`);
    }
  };

  // =========================
  // Attachments normalizador
  // =========================
  const normalizeAttachment = (att) => {
    if (!att) return null;

    if (typeof att === "string") {
      const s = att.trim();
      if (!s) return null;

      if ((s.startsWith("{") && s.endsWith("}")) || (s.startsWith("[") && s.endsWith("]"))) {
        try {
          return normalizeAttachment(JSON.parse(s));
        } catch {
          return { url: s, name: s.split("/").pop() || "documento.pdf" };
        }
      }

      return { url: s, name: s.split("/").pop() || "documento.pdf" };
    }

    if (typeof att === "object") {
      const url = String(att.url ?? att.path ?? att.href ?? "").trim();
      if (!url) return null;
      const name = String(att.name ?? att.filename ?? url.split("/").pop() ?? "documento.pdf");
      return { url, name };
    }

    return null;
  };

  const parseAttachmentsFlexible = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw.map(normalizeAttachment).filter(Boolean);

    if (typeof raw === "string") {
      const s = raw.trim();
      if (!s) return [];
      try {
        const parsed = JSON.parse(s);
        if (Array.isArray(parsed)) return parsed.map(normalizeAttachment).filter(Boolean);
        const one = normalizeAttachment(parsed);
        return one ? [one] : [];
      } catch {
        const one = normalizeAttachment(s);
        return one ? [one] : [];
      }
    }

    const one = normalizeAttachment(raw);
    return one ? [one] : [];
  };

  const normalizeStatus = (v) => String(v ?? "").trim().toLowerCase();

  const statusUI = useMemo(() => {
    const rs = normalizeStatus(solicitud?.response_status ?? solicitud?.status ?? solicitud?.estado ?? "");
    const responded = Number(solicitud?.responded ?? 0) === 1;

    const isApproved = rs === "aprobado" || rs === "approved" || rs === "respondido";
    const isRejected = rs === "negado" || rs === "rechazado" || rs === "rejected" || rs === "denied";

    // si responded=1 pero status vacío, igual lo tratamos como respondido
    const isAnswered = responded || isApproved || isRejected;

    return {
      isAnswered,
      isApproved,
      isRejected,
      label: isApproved ? "Aprobado" : isRejected ? "Rechazado" : "Pendiente",
      raw: rs,
    };
  }, [solicitud]);

  // ========= GET solicitud por contract_id =========
  const fetchAlmacenSolicitudByContractId = async (contractId) => {
    const cid = normalizeId(contractId);
    if (!cid) return null;

    const endpoint = apiConfig?.endpoints?.almacenSolicitudes || "/api/almacen/solicitudes/index.php";
    const url = apiConfig.getUrl(endpoint, `contract_id=${encodeURIComponent(cid)}`);

    const resp = await fetch(url, {
      method: "GET",
      credentials: "include",
      headers: { Accept: "application/json" },
    });

    const data = await safeJson(resp);
    if (!resp.ok) {
      throw new Error(
        data?.error ||
          data?.message ||
          data?.raw ||
          `Error cargando solicitud almacén (HTTP ${resp.status})`
      );
    }

    if (Array.isArray(data) && data.length > 0) return data[0];
    return null;
  };

  // ========= UPLOAD PDF =========
  const uploadPdfToServer = async ({ blob, filename, contractId }) => {
    const cid = normalizeId(contractId);
    if (!cid) throw new Error("Falta contract_id para subir PDF.");

    const uploadEndpoint = apiConfig?.endpoints?.almacenUploadPdf || "/api/uploads/almacen/upload.php";
    const url = apiConfig.getUrl(uploadEndpoint);

    const fd = new FormData();
    fd.append("contract_id", cid);
    fd.append("file", blob, filename);

    const resp = await fetch(url, {
      method: "POST",
      credentials: "include",
      body: fd,
    });

    const data = await safeJson(resp);

    if (!resp.ok || data?.ok === false) {
      throw new Error(data?.error || data?.message || data?.raw || `Error subiendo PDF (HTTP ${resp.status})`);
    }

    if (!data?.url) throw new Error("El upload no devolvió url del archivo.");
    return data; // { ok:true, url, name }
  };

  // ========= SAVE RESPUESTA BD =========
 const saveAlmacenResponse = async ({
  requestId,
  contractId,
  responseStatus, // "aprobado" | "negado"
  motivoRechazo,
  valorEstimadoVigencia,
  responseAttachments, // array [{url,name}] o string JSON
  notes,
}) => {
  const rid = normalizeId(requestId);
  const cid = normalizeId(contractId);
  if (!cid) throw new Error("Falta contract_id para guardar respuesta.");

  const endpoint =
    apiConfig?.endpoints?.almacenSolicitudes || "/api/almacen/solicitudes/index.php";
  const url = apiConfig.getUrl(endpoint);

  const status = String(responseStatus || "").toLowerCase().trim();
  if (status !== "aprobado" && status !== "negado") {
    throw new Error("responseStatus inválido. Debe ser 'aprobado' o 'negado'.");
  }

  // ✅ Validaciones
  let valorNum = null;
  if (status === "aprobado") {
    valorNum = parseNumberCO(valorEstimadoVigencia);
    if (!Number.isFinite(valorNum) || valorNum <= 0) {
      throw new Error("VALOR ESTIMADO EN LA VIGENCIA ACTUAL debe ser > 0 para APROBAR.");
    }
  }

  const motivo = String(motivoRechazo || "").trim();
  if (status === "negado" && !motivo) {
    throw new Error("Debes escribir el motivo del rechazo.");
  }

  // ✅ Adjuntos normalizados a JSON string (si aplica)
  const respAtt =
    responseAttachments == null
      ? null
      : typeof responseAttachments === "string"
      ? responseAttachments
      : JSON.stringify(responseAttachments);

  const payload = {
    contract_id: cid,
    contractId: cid,

    // ✅ manda id por si el backend lo usa; si no, cae a última por contrato
    request_id: rid || null,
    almacen_request_id: rid || null,
    id: rid || null,

    response_status: status,
    status,
    estado: status,
    responded: 1,

    ...(status === "aprobado"
      ? { valor_estimado_vigencia: valorNum, valorEstimadoVigencia: valorNum }
      : {}),

    ...(status === "negado" ? { motivo_rechazo: motivo } : {}),

    ...(respAtt ? { response_attachments: respAtt } : {}),

    ...(notes ? { notes: String(notes) } : {}),

    // info
    responded_at: new Date().toISOString().slice(0, 19).replace("T", " "),
  };

  // ✅ IMPORTANTE: usar POST (evita bloqueos/limitaciones con PUT en hosting/proxy)
  const resp = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(payload),
  });

  const data = await safeJson(resp);

  if (!resp.ok) {
    throw new Error(
      data?.error || data?.message || data?.raw || `Error guardando respuesta (HTTP ${resp.status})`
    );
  }

  if (data && typeof data === "object" && "ok" in data && data.ok === false) {
    throw new Error(data?.error || "No se pudo guardar la respuesta.");
  }

  return data;
};

  // ================= CARGAR DATOS BASE (y SIEMPRE solicitud actualizada) =================
  useEffect(() => {
    if (!isOpen || !contractData) return;

    let isCancelled = false;

    const run = async () => {
      try {
        setError("");
        setMensaje("");
        setLoading(true);

        const almacenRequestId =
          contractData?.almacen_request_id ||
          contractData?.almacenRequestId ||
          contractData?.request_id ||
          contractData?.id ||
          "";

        const contractIdFromRow =
          contractData?.contract_id ||
          contractData?.contractId ||
          contractData?.id_contract ||
          contractData?.contract_id_fk ||
          contractData?.id ||
          "";

        const contractIdNormalized = normalizeId(contractIdFromRow);
        if (!contractIdNormalized) {
          throw new Error("No llegó contract_id (ni id).");
        }

        // ✅ SIEMPRE traer la última solicitud (para estado + adjuntos + motivo)
        const fetchedSolicitud = await fetchAlmacenSolicitudByContractId(contractIdNormalized);

        if (!isCancelled) {
          setSolicitud(fetchedSolicitud || null);
        }

        // Usamos el contrato que ya tienes, pero si el backend trae más campos, los mezclamos
        let rawContract = contractData;
        if (fetchedSolicitud && typeof fetchedSolicitud === "object") {
          rawContract = { ...(rawContract || {}), ...fetchedSolicitud };
        }

        const rawFormData = rawContract?.form_data ?? rawContract?.formData ?? rawContract?.form ?? null;

        let parsedForm = null;
        if (rawFormData && typeof rawFormData === "object") parsedForm = rawFormData;
        else if (rawFormData && typeof rawFormData === "string") {
          const t = rawFormData.trim();
          if (t) {
            try {
              const d = JSON.parse(t);
              if (d && typeof d === "object") parsedForm = d;
            } catch {
              parsedForm = null;
            }
          }
        }

        const contract = {
          ...(rawContract || {}),
          _form_data: parsedForm,
          almacen_request_id: almacenRequestId,
          contract_id: contractIdNormalized,
          contractId: contractIdNormalized,
          id: rawContract?.id || contractIdNormalized,
        };

        const hoyISO = new Date().toISOString().slice(0, 10);

        const valorBase =
          contract?.valor ??
          contract?.valor_solicitado ??
          contract?.valorSolicitado ??
          contract?.presupuesto_total ??
          contract?.valorContrato ??
          "";

        const duracionEstimada = contract?.duracion_estimada ?? contract?.duracion ?? contract?.duracionEstimada ?? "";

        const modalidadSeleccion = contract?.modalidad_seleccion ?? contract?.modalidadSeleccion ?? contract?.modalidad ?? "";

        const naturalezaGasto = contract?.naturaleza_gasto ?? contract?.fuente_recursos ?? contract?.fuenteRecursos ?? "";

        const nombreSolicitante = contract?.nombre_solicitante ?? contract?.responsable ?? contract?.nombre ?? "";
        const secretaria = contract?.secretaria ?? contract?.dependencia ?? "";
        const objetoContractual = contract?.objeto_contractual ?? contract?.objeto ?? contract?.objetoContrato ?? "";

        const radicado = contract?.radicado ?? contract?.certificado_numero ?? contract?.certificadoNumero ?? "";

        const unspscFromForm =
          contract?._form_data?.unspsc ??
          contract?._form_data?.codigo_unspsc ??
          contract?._form_data?.codigoUnspsc ??
          "";

        const descFromForm =
          contract?._form_data?.descripcion ??
          contract?._form_data?.descripcion_codigo ??
          contract?._form_data?.descripcionCodigo ??
          contract?._form_data?.descripcion_unspsc ??
          "";

        const correoFromForm =
          contract?._form_data?.correo ??
          contract?._form_data?.correo_solicitante ??
          contract?._form_data?.correoSolicitante ??
          "";

        const contactoFromForm =
          contract?._form_data?.contacto ??
          contract?._form_data?.telefono ??
          contract?._form_data?.celular ??
          "";

        const unspsc = String(unspscFromForm ?? contract?.unspsc ?? contract?.codigo_unspsc ?? "").trim();
        const descripcion = String(descFromForm ?? contract?.descripcion ?? contract?.descripcion_codigo ?? "").trim();
        const correo = String(correoFromForm ?? contract?.correo ?? contract?.correo_solicitante ?? "").trim();
        const contacto = String(contactoFromForm || contract?.contacto || "6042740069").trim() || "6042740069";

        const valorEstimadoVigencia = contract?.valor_estimado_vigencia ?? contract?.valorEstimadoVigencia ?? contract?.valor_estimado ?? "";

        const base = {
          certificadoNumero: radicado,
          duracion: duracionEstimada ? `${duracionEstimada} días` : "",
          modalidad: modalidadSeleccion,
          fuenteRecursos: naturalezaGasto,
          valorSolicitado: formatNumberCO(valorBase),
          responsable: nombreSolicitante,
          dependencia: secretaria,
          objetoContrato: objetoContractual,

          unspsc,
          descripcion,
          correo,
          contacto,

          valorEstimadoVigencia: formatNumberCO(valorEstimadoVigencia),
          fechaExpedicion: hoyISO,

          firmaUrl: "",
          nombreAlmacenista: "HÉCTOR JAIME ACEVEDO ECHEVERRY",

          almacenRequestId: normalizeId(almacenRequestId),
          contractId: contractIdNormalized,
        };

        if (!isCancelled) setFormData(base);
      } catch (e) {
        if (!isCancelled) {
          setError(e?.message || "Error cargando datos.");
          setFormData(null);
          setSolicitud(null);
        }
      } finally {
        if (!isCancelled) setLoading(false);
      }
    };

    run();
    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, contractData]);

  // ================= HANDLERS =================
  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...(prev || {}), [field]: value }));
  };

  const handleMoneyBlur = (field) => {
    setFormData((prev) => ({ ...(prev || {}), [field]: formatNumberCO(prev?.[field]) }));
  };

  const validateForApprove = () => {
    if (!formData) return { ok: false, msg: "No hay datos cargados." };

    const correo = String(formData.correo || "").trim();
    const valorStr = String(formData.valorEstimadoVigencia || "").trim();

    if (!formData.unspsc?.trim()) return { ok: false, msg: "Falta CÓDIGO UNSPSC." };
    if (!formData.descripcion?.trim()) return { ok: false, msg: "Falta DESCRIPCIÓN." };
    if (!correo) return { ok: false, msg: "Falta CORREO." };
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(correo)) return { ok: false, msg: "El CORREO no es válido." };

    if (!valorStr) return { ok: false, msg: "VALOR ESTIMADO EN LA VIGENCIA ACTUAL es obligatorio." };
    const numeric = parseNumberCO(valorStr);
    if (!Number.isFinite(numeric) || numeric <= 0) return { ok: false, msg: "VALOR ESTIMADO debe ser > 0." };

    if (!formData.firmaUrl) return { ok: false, msg: "Debe subir la firma antes de RESPONDER." };

    return { ok: true, msg: "" };
  };

  const handleSignatureUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError("");
    setMensaje("");

    if (!file.type.startsWith("image/")) {
      setError("Seleccione una imagen (JPG/PNG/WebP).");
      e.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      handleChange("firmaUrl", reader.result);
      setMensaje("Firma cargada. Ahora puedes RESPONDER.");
    };
    reader.readAsDataURL(file);
  };

 const handleRechazar = async () => {
  try {
    if (!formData) return;

    const requestId = normalizeId(
      solicitud?.id ||
      formData?.almacenRequestId ||
      contractData?.almacen_request_id ||
      contractData?.request_id ||
      ""
    );

    const contractId = normalizeId(formData.contractId);

    if (!requestId) {
      throw new Error("No se encontró id de la solicitud de almacén.");
    }

    const motivo = String(motivoRechazo || "").trim();
    if (!motivo) {
      setError("Debes escribir el motivo del rechazo.");
      return;
    }

    setLoading(true);
    setError("");
    setMensaje("");

    await saveAlmacenResponse({
      requestId,
      contractId,
      responseStatus: "negado",
      motivoRechazo: motivo,
      responseAttachments: null,
    });

    const refreshed = await fetchAlmacenSolicitudByContractId(contractId);
    setSolicitud(refreshed || null);

    onResponded?.(requestId, {
      source: "almacen",
      requestId,
      contractId,
      status: "negado",
      responded: true,
    });

    closeModalSafe();
  } catch (e) {
    setError(e?.message || "No se pudo rechazar.");
  } finally {
    setLoading(false);
  }
};

  const handleDescargarSolo = async () => {
    if (!formData) return;

    const v = validateForApprove();
    if (!v.ok) {
      setError(v.msg);
      return;
    }

    try {
      setLoading(true);
      setError("");
      setMensaje("");
      setIsPrinting(true);

      await new Promise((res) => setTimeout(res, 200));

      const element = modalRef.current;
      if (!element) throw new Error("No se encontró el contenido del certificado.");

      const filename = `certificado_${formData?.certificadoNumero || "sin_radicado"}.pdf`;

      const opt = {
        margin: 10,
        filename,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "mm", format: "letter", orientation: "portrait" },
      };

      const pdfBlob = await html2pdf().set(opt).from(element).output("blob");

      const urlDescarga = URL.createObjectURL(pdfBlob);
      const a = document.createElement("a");
      a.href = urlDescarga;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(urlDescarga);

      setMensaje("PDF descargado. Ahora haz clic en RESPONDER para guardar la respuesta.");
    } catch (err) {
      setError("Error al descargar: " + (err?.message || "desconocido"));
    } finally {
      setIsPrinting(false);
      setLoading(false);
    }
  };
const handleResponder = async () => {
  if (!formData) return;

  const v = validateForApprove();
  if (!v.ok) {
    setError(v.msg);
    return;
  }

  try {
    setLoading(true);
    setError("");
    setMensaje("");
    setIsPrinting(true);

    await new Promise((res) => setTimeout(res, 200));

    const element = modalRef.current;
    if (!element) throw new Error("No se encontró el contenido del certificado.");

    const filename = `certificado_${formData?.certificadoNumero || "sin_radicado"}.pdf`;

    const opt = {
      margin: 10,
      filename,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: "mm", format: "letter", orientation: "portrait" },
    };

    // 1️⃣ Generar PDF
    const pdfBlob = await html2pdf().set(opt).from(element).output("blob");

    // 2️⃣ IDs (AQUÍ ESTABA EL BUG)
    const requestId = normalizeId(
      solicitud?.id ||
      formData?.almacenRequestId ||
      contractData?.almacen_request_id ||
      contractData?.request_id ||
      ""
    );

    const contractId = normalizeId(formData.contractId);

    if (!requestId) {
      throw new Error("No se encontró id de la solicitud de almacén.");
    }

    // 3️⃣ Subir PDF
    const upload = await uploadPdfToServer({ blob: pdfBlob, filename, contractId });

    const responseAttachments = [
      { url: upload.url, name: upload.name || filename },
    ];

    // 4️⃣ Guardar respuesta
    await saveAlmacenResponse({
      requestId,
      contractId,
      responseStatus: "aprobado",
      valorEstimadoVigencia: formData.valorEstimadoVigencia,
      responseAttachments,
    });

    const refreshed = await fetchAlmacenSolicitudByContractId(contractId);
    setSolicitud(refreshed || null);

    onResponded?.(requestId, {
      source: "almacen",
      requestId,
      contractId,
      status: "aprobado",
      responded: true,
      attachments: responseAttachments,
    });

    closeModalSafe();
  } catch (err) {
    setError("Error al responder: " + (err?.message || "desconocido"));
  } finally {
    setIsPrinting(false);
    setLoading(false);
  }
};


  if (!isOpen) return null;

  const vigenciaYear = (() => {
    if (formData?.fechaExpedicion) {
      const d = new Date(formData.fechaExpedicion);
      if (!isNaN(d.getTime())) return d.getFullYear();
    }
    return new Date().getFullYear();
  })();

  const solicitudBienes = {
    id: formData?.contractId || contractData?.contract_id || contractData?.id_contract || contractData?.id,
    radicado: formData?.certificadoNumero || contractData?.radicado,
    secretaria: formData?.dependencia || contractData?.secretaria,
    notes: "",
  };

  // ✅ Adjuntos de respuesta listos para UI
  const responseFiles = parseAttachmentsFlexible(
    solicitud?.response_attachments ?? solicitud?.responseAttachments ?? null
  );

  const motivoMostrar =
    String(
      solicitud?.motivo_rechazo ??
        solicitud?.motivoRechazo ??
        solicitud?.rejection_reason ??
        ""
    ).trim();

  const respondedAt =
    solicitud?.responded_at ??
    solicitud?.respondedAt ??
    solicitud?.updated_at ??
    "";

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "linear-gradient(to bottom, rgba(30,58,138,0.9), rgba(30,64,175,0.9))",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
        zIndex: 9999,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "980px",
          backgroundColor: "white",
          borderRadius: isPrinting ? "0" : "8px",
          boxShadow: isPrinting ? "none" : "0 10px 25px rgba(0,0,0,0.25)",
          border: isPrinting ? "2px solid #000" : "none",
          padding: isPrinting ? "18px 26px" : "24px 32px",
          display: "flex",
          flexDirection: "column",
          maxHeight: isPrinting ? "none" : "95vh",
          fontFamily: "Arial, sans-serif",
          overflow: "hidden",
        }}
      >
        {!isPrinting && (
          <>
            {error && (
              <div
                style={{
                  marginBottom: "12px",
                  padding: "8px 12px",
                  backgroundColor: "#fef2f2",
                  color: "#b91c1c",
                  fontSize: "12px",
                  borderRadius: "4px",
                }}
              >
                {error}
              </div>
            )}

            {mensaje && (
              <div
                style={{
                  marginBottom: "12px",
                  padding: "8px 12px",
                  backgroundColor: "#ecfdf5",
                  color: "#15803d",
                  fontSize: "12px",
                  borderRadius: "4px",
                }}
              >
                {mensaje}
              </div>
            )}

            {/* ✅ SECCIÓN RESPUESTA (LO QUE TE FALTABA) */}
            <div
              style={{
                marginBottom: 12,
                padding: "10px 12px",
                borderRadius: 6,
                border: "1px solid #e5e7eb",
                background: statusUI.isApproved ? "#ecfdf5" : statusUI.isRejected ? "#fef2f2" : "#fffbeb",
                color: statusUI.isApproved ? "#166534" : statusUI.isRejected ? "#991b1b" : "#854d0e",
                fontSize: 13,
              }}
            >
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <strong>Estado:</strong>
                <span style={{ fontWeight: 800 }}>{statusUI.label}</span>

                {statusUI.isAnswered && respondedAt ? (
                  <span style={{ color: "#6b7280", fontWeight: 600 }}>
                    Respondido: {String(respondedAt).slice(0, 10)}
                  </span>
                ) : null}
              </div>

              {statusUI.isRejected && motivoMostrar ? (
                <div style={{ marginTop: 6 }}>
                  <strong>Motivo:</strong> <span style={{ fontStyle: "italic" }}>{motivoMostrar}</span>
                </div>
              ) : null}

              {responseFiles.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>📥 Documento(s) de respuesta:</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {responseFiles.map((f, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => handleDownload(f.url, f.name)}
                        style={{
                          padding: "8px 12px",
                          backgroundColor: "#10b981",
                          color: "white",
                          borderRadius: 6,
                          border: "none",
                          cursor: "pointer",
                          fontWeight: 700,
                          fontSize: 13,
                        }}
                      >
                        📄 {f.name || `Respuesta ${i + 1}`}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        <div ref={modalRef} style={{ display: "flex", flexDirection: "column", height: "100%" }}>
          {/* ENCABEZADO */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: "12px",
            }}
          >
            <div style={{ width: "150px" }}>
              <img
                src={logo}
                alt="Alcaldía de Copacabana"
                style={{ maxWidth: "100%", height: "auto", objectFit: "contain" }}
              />
            </div>

            <div
              style={{
                flex: 1,
                textAlign: "center",
                fontWeight: "bold",
                textTransform: "uppercase",
                fontSize: "13px",
                paddingTop: "10px",
              }}
            >
              CERTIFICADO DE ADQUISICIÓN DE BIENES Y/O
              <br />
              PRESTACIÓN DE SERVICIOS
            </div>

            <div
              style={{
                fontSize: "9px",
                textAlign: "left",
                marginLeft: "12px",
                paddingTop: "8px",
                lineHeight: 1.4,
              }}
            >
              <div>
                <strong>Código:</strong> A-GB-F-008
              </div>
              <div>
                <strong>Versión:</strong> 02
              </div>
              <div>
                <strong>Fecha Actualización:</strong> 12/04/2024
              </div>
            </div>
          </div>

          <div style={{ textAlign: "center", fontWeight: "bold", marginBottom: "8px", fontSize: "11px" }}>
            EL ALMACENISTA GENERAL DEL MUNICIPIO DE COPACABANA
            <br />
            CERTIFICA QUE
          </div>

          <p style={{ textAlign: "justify", marginBottom: "10px", fontSize: "11px" }}>
            Dentro del "PLAN ANUAL DE ADQUISICIONES" del Municipio de Copacabana para la vigencia {vigenciaYear} se
            encuentra registrada la siguiente necesidad de contratación de prestación de servicios:
          </p>

          <div style={{ flex: 1, overflowY: isPrinting ? "visible" : "auto", paddingRight: isPrinting ? 0 : 4 }}>
            {!formData ? (
              <p style={{ fontSize: "14px", color: "#4b5563" }}>{loading ? "Cargando información..." : "Sin datos."}</p>
            ) : (
              <>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "10px", marginBottom: "12px" }}>
                  <tbody>
                    {[
                      ["CERTIFICADO N°", "certificadoNumero", { type: "text", editable: false }],
                      ["CÓDIGO UNSPSC:*", "unspsc", { type: "text", editable: false }],
                      ["DESCRIPCIÓN:*", "descripcion", { type: "textarea", editable: false }],
                      ["DURACIÓN ESTIMADA DEL CONTRATO", "duracion", { type: "text", editable: false }],
                      ["MODALIDAD DE SELECCIÓN:", "modalidad", { type: "text", editable: false }],
                      ["FUENTE DE LOS RECURSOS:", "fuenteRecursos", { type: "text", editable: false }],
                      ["VALOR ESTIMADO EN LA VIGENCIA ACTUAL:*", "valorEstimadoVigencia", { type: "text", editable: true }],
                      ["VALOR SOLICITADO", "valorSolicitado", { type: "text", editable: false }],
                      ["RESPONSABLE", "responsable", { type: "text", editable: false }],
                      ["DEPENDENCIA", "dependencia", { type: "text", editable: false }],
                      ["CORREO ELECTRÓNICO DE LA DEPENDENCIA:*", "correo", { type: "text", editable: false }],
                      ["CONTACTO", "contacto", { type: "text", editable: false }],
                      ["FECHA DE EXPEDICIÓN", "fechaExpedicion", { type: "date", editable: false }],
                    ].map(([label, field, meta]) => {
                      const isEditable = esAlmacen && meta?.editable && !loading;
                      const inputReadOnly = !isEditable;
                      const type = meta?.type;

                      return (
                        <tr key={field}>
                          <td style={{ width: "40%", border: "1px solid #000", padding: "3px 4px", fontWeight: "bold" }}>
                            {label}
                          </td>
                          <td style={{ border: "1px solid #000", padding: "3px 4px" }}>
                            {isPrinting ? (
                              <div style={{ whiteSpace: "pre-line", fontSize: "10px" }}>
                                {moneyFields.has(field) ? formatNumberCO(formData[field]) : formData[field] || ""}
                              </div>
                            ) : type === "textarea" ? (
                              <textarea
                                value={formData[field] || ""}
                                readOnly
                                onChange={() => {}}
                                style={{
                                  width: "100%",
                                  minHeight: "40px",
                                  border: "none",
                                  outline: "none",
                                  fontSize: "11px",
                                  resize: "vertical",
                                  backgroundColor: "#f9fafb",
                                }}
                              />
                            ) : (
                              <input
                                type={type === "date" ? "date" : "text"}
                                value={formData[field] || ""}
                                readOnly={inputReadOnly}
                                onChange={(e) => handleChange(field, e.target.value)}
                                onBlur={() => {
                                  if (moneyFields.has(field)) handleMoneyBlur(field);
                                }}
                                style={{
                                  width: "100%",
                                  border: isEditable ? "1px solid #111827" : "none",
                                  outline: "none",
                                  fontSize: "11px",
                                  backgroundColor: isEditable ? "white" : "#f9fafb",
                                  padding: isEditable ? "3px 6px" : "0",
                                  borderRadius: isEditable ? "3px" : "0",
                                }}
                              />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {isPrinting && (
                  <>
                    <p style={{ marginTop: "6px", marginBottom: "6px", fontSize: "10px" }}>
                      La información precitada es requerida para efectos de celebración del contrato cuyo objeto es:
                    </p>

                    <div style={{ fontSize: "10px", whiteSpace: "pre-line", textAlign: "justify" }}>
                      {formData.objetoContrato || ""}
                    </div>

                    <p style={{ marginTop: "10px", fontSize: "9px", textAlign: "justify" }}>
                      Se expide esta certificación en cumplimiento de lo establecido en el Decreto 1082 de 2015 del
                      Departamento Nacional de Planeación y la Circular Externa No 2 de 16 de agosto de 2013 Colombia
                      Compra Eficiente.
                    </p>

                    <div style={{ marginTop: "22px", textAlign: "center" }}>
                      <div
                        style={{
                          width: "60%",
                          margin: "0 auto",
                          position: "relative",
                          paddingTop: "34px",
                          paddingBottom: "6px",
                          borderBottom: "1px solid #000",
                        }}
                      >
                        {formData.firmaUrl && (
                          <img
                            src={formData.firmaUrl}
                            alt="Firma digital"
                            style={{
                              position: "absolute",
                              left: "50%",
                              transform: "translateX(-50%)",
                              bottom: "-8px",
                              maxWidth: "180px",
                              maxHeight: "75px",
                              objectFit: "contain",
                              opacity: 0.92,
                              pointerEvents: "none",
                            }}
                          />
                        )}
                      </div>

                      <div style={{ marginTop: "8px", fontWeight: "bold", fontSize: "10px", lineHeight: 1.25 }}>
                        {formData.nombreAlmacenista}
                        <br />
                        Almacenista
                        <div style={{ marginTop: "6px", fontWeight: 600, fontSize: "9px" }}>
                          Expedición de certificados de PAA – {vigenciaYear}
                          <br />
                          Municipio de Copacabana - Antioquia
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {/* FOOTER UI */}
        {!isPrinting && (
          <div
            style={{
              marginTop: "14px",
              borderTop: "1px solid #e5e7eb",
              paddingTop: "10px",
              display: "flex",
              justifyContent: "flex-end",
              gap: "0.5rem",
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <button
              type="button"
              onClick={closeModalSafe}
              style={{
                padding: "8px 14px",
                backgroundColor: "#e5e7eb",
                color: "#374151",
                borderRadius: "4px",
                border: "none",
                fontSize: "14px",
                cursor: "pointer",
              }}
              disabled={loading}
            >
              Cerrar
            </button>

            {esAlmacen && (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setError("");
                    setMensaje("");
                    setMotivoRechazo("");
                    setShowReject(true);
                  }}
                  style={{
                    padding: "8px 16px",
                    backgroundColor: "#dc2626",
                    color: "white",
                    borderRadius: "4px",
                    border: "none",
                    fontSize: "14px",
                    fontWeight: 700,
                    cursor: loading ? "not-allowed" : "pointer",
                  }}
                  disabled={loading}
                >
                  Rechazar
                </button>

                <button
                  type="button"
                  onClick={handleDescargarSolo}
                  style={{
                    padding: "8px 16px",
                    backgroundColor: "#2563eb",
                    color: "white",
                    borderRadius: "4px",
                    border: "none",
                    fontSize: "14px",
                    fontWeight: 700,
                    cursor: loading ? "not-allowed" : "pointer",
                  }}
                  disabled={loading}
                >
                  {loading ? "Procesando..." : "Descargar PDF"}
                </button>

                <button
                  type="button"
                  onClick={handleResponder}
                  style={{
                    padding: "8px 16px",
                    backgroundColor: "#16a34a",
                    color: "white",
                    borderRadius: "4px",
                    border: "none",
                    fontSize: "14px",
                    fontWeight: 800,
                    cursor: loading ? "not-allowed" : "pointer",
                  }}
                  disabled={loading}
                >
                  {loading ? "Respondiendo..." : "Responder"}
                </button>

                <input
                  type="file"
                  accept="image/*"
                  onChange={handleSignatureUpload}
                  style={{ fontSize: "12px", alignSelf: "center" }}
                  disabled={loading}
                  title="Subir firma"
                />
              </>
            )}
          </div>
        )}
      </div>

      {/* MODAL RECHAZO */}
      {showReject && !isPrinting && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10001,
            padding: "1rem",
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 520,
              background: "white",
              borderRadius: 8,
              padding: 16,
              boxShadow: "0 10px 25px rgba(0,0,0,0.25)",
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: 8 }}>Rechazar solicitud</h3>
            <p style={{ marginTop: 0, color: "#6b7280", fontSize: 13 }}>
              Escribe el motivo del rechazo (esto lo verá el solicitante).
            </p>

            <textarea
              value={motivoRechazo}
              onChange={(e) => setMotivoRechazo(e.target.value)}
              style={{
                width: "100%",
                minHeight: 110,
                padding: 10,
                borderRadius: 6,
                border: "1px solid #e5e7eb",
                outline: "none",
              }}
            />

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
              <button
                type="button"
                onClick={() => setShowReject(false)}
                disabled={loading}
                style={{
                  padding: "8px 12px",
                  background: "#e5e7eb",
                  border: "none",
                  borderRadius: 6,
                  cursor: "pointer",
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleRechazar}
                disabled={loading}
                style={{
                  padding: "8px 12px",
                  background: "#dc2626",
                  color: "white",
                  border: "none",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontWeight: 700,
                }}
              >
                {loading ? "Guardando..." : "Confirmar rechazo"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showResponderAlmacen && (
        <EnviarAlmacen
          userRole={userRole}
          solicitud={solicitudBienes}
          onClose={() => setShowResponderAlmacen(false)}
          onSubmit={() => setShowResponderAlmacen(false)}
        />
      )}
    </div>
  );
}
